import React from 'react'
import { Form, Formik } from 'formik'
import { FormattedMessage, useIntl } from 'react-intl'
import * as Yup from 'yup'
import IconAdd from '@aboutbits/react-material-icons/dist/IconAdd'
import classNames from 'classnames'
import IconRemove from '@aboutbits/react-material-icons/dist/IconRemove'
import { DialogHeaderArea } from '../../../../../shared/dialog/DialogHeaderArea'
import { HeaderLeftArea } from '../../../../../shared/header'
import { DialogAction } from '../../../../../shared/dialog/DialogAction'
import { IconClose } from '../../../../../shared/svgs'
import {
  Section,
  SectionItemForm,
  SectionList,
  SectionTitle,
} from '../../../../../shared/section'
import { InputWithLabel } from '../../../../../shared/form'
import { SectionActions } from '../../../../../shared/section/SectionActions'
import { CancelButton } from '../../../../../shared/form/button/CancelButton'
import { SubmitButton } from '../../../../../shared/form/button/SubmitButton'
import { DialogTitle } from '../../../../../shared/dialog/DialogTitle'
import { ButtonVariant } from '../../../../../shared/button'
import { translationsShared } from '../../../../../shared/translations/translationsShared'
import { translationsActivity } from '../../../translations/translationsActivity'
import { useGetArticleById } from '../../../db/article'
import {
  isValidNumber,
  parseNumber,
} from '../../../../../shared/utils/numberUtils'
import { ContentArea } from '../../../../../shared/content'
import { ButtonIcon } from '../../../../../shared/button/Button'

const ArticleAmountView: React.FC<{
  onDismiss: (event?: React.SyntheticEvent<Element, Event> | undefined) => void
  onConfirmAndNew?: (amount: string, descriptionText: string) => void
  onConfirm: (amount: string, descriptionText: string) => void
  amount?: string
  descriptionText?: string
  articleId: string
}> = ({
  onConfirm,
  onConfirmAndNew,
  onDismiss,
  amount = '1',
  descriptionText = '',
  articleId,
}) => {
  const intl = useIntl()
  const { data: article } = useGetArticleById(articleId)
  const [countIsValidNumber, setCountIsValidNumber] = React.useState(true)

  const initialValue: {
    count: string
    description: string
  } = {
    count: amount,
    description: descriptionText ?? '',
  }

  const schema = Yup.object().shape({
    count: Yup.string().required(
      intl.formatMessage(translationsShared.validationRequired)
    ),
  })

  const [submitAction, setSubmitAction] = React.useState<
    'submitAndClose' | 'submitAndNew'
  >('submitAndClose')

  const handleSubmit = (values: { count: string; description: string }) => {
    if (submitAction === 'submitAndClose' || onConfirmAndNew === undefined) {
      onConfirm(values.count, values.description)
    } else {
      onConfirmAndNew?.(values.count, values.description)
    }
  }

  return (
    <>
      <DialogHeaderArea>
        <HeaderLeftArea>
          <DialogAction
            label={intl.formatMessage(translationsShared.goBack)}
            onClick={onDismiss}
            className="rounded-full bg-white hover:bg-gray-300"
          >
            <IconClose className="size-7 fill-current text-gray-700" />
          </DialogAction>
        </HeaderLeftArea>
        <DialogTitle className="text-white">
          <FormattedMessage {...translationsActivity.articleAmountTitle} />
        </DialogTitle>
      </DialogHeaderArea>
      <ContentArea>
        <Formik
          initialValues={initialValue}
          onSubmit={(values) => {
            handleSubmit(values)
          }}
          validationSchema={schema}
          validateOnChange={true}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Section>
                <SectionTitle>{article ? article.name : ''}</SectionTitle>
                <SectionList>
                  <SectionItemForm>
                    <div className={'flex flex-row items-end gap-3'}>
                      <ButtonIcon
                        icon={IconRemove}
                        label={intl.formatMessage({
                          id: 'activity.form.article.dialog.decreaseAmount',
                          defaultMessage: 'Decrease amount',
                        })}
                        className={classNames(
                          'border-2 border-red-500',
                          !countIsValidNumber && 'opacity-40'
                        )}
                        iconClassName={classNames('fill-current text-red-500')}
                        disabled={!countIsValidNumber}
                        onClick={() => {
                          const currentCount = parseNumber(values.count)
                          if (!Number.isNaN(currentCount)) {
                            setFieldValue(
                              'count',
                              intl.formatNumber(currentCount - 1)
                            )
                          }
                        }}
                      />
                      <InputWithLabel
                        id="count"
                        name="count"
                        type="text"
                        label={intl.formatMessage({
                          id: 'activity.article.amount',
                          defaultMessage: 'Amount',
                        })}
                        onChange={(e) => {
                          const value = e.target.value
                          setCountIsValidNumber(isValidNumber(value))
                        }}
                      />
                      <ButtonIcon
                        icon={IconAdd}
                        label={intl.formatMessage({
                          id: 'activity.form.article.dialog.increaseAmount',
                          defaultMessage: 'Increase amount',
                        })}
                        className={classNames(
                          'border-2 border-green-500',
                          !countIsValidNumber && 'opacity-40'
                        )}
                        iconClassName={'fill-current text-green-500'}
                        disabled={!countIsValidNumber}
                        onClick={() => {
                          const currentCount = parseNumber(values.count)
                          if (!Number.isNaN(currentCount)) {
                            setFieldValue(
                              'count',
                              intl.formatNumber(currentCount + 1)
                            )
                          }
                        }}
                      />
                    </div>
                    <InputWithLabel
                      id="description"
                      name="description"
                      type="string"
                      label={intl.formatMessage({
                        id: 'activity.article.description',
                        defaultMessage: 'Description',
                      })}
                    />
                  </SectionItemForm>
                </SectionList>
              </Section>
              <SectionActions className="py-5 md:py-5">
                <CancelButton
                  variant={ButtonVariant.dialogSecondary}
                  onClick={onDismiss}
                />
                {onConfirmAndNew && (
                  <SubmitButton
                    variant={ButtonVariant.dialogPrimary}
                    onClick={() => setSubmitAction('submitAndNew')}
                  >
                    <FormattedMessage
                      id="activity.form.article.dialog.saveAndNew"
                      defaultMessage="Save & New"
                    />
                  </SubmitButton>
                )}
                <SubmitButton variant={ButtonVariant.dialogPrimary} />
              </SectionActions>
            </Form>
          )}
        </Formik>
      </ContentArea>
    </>
  )
}

export { ArticleAmountView }
